import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PlayArrow,
  Pause,
  CheckCircle,
  Cancel,
  Schedule,
  Warning
} from '@mui/icons-material';
import { plannedExpensesAPI, categoriesAPI, banksAPI, authAPI, Bank } from '../services/api';

interface PlannedExpense {
  id: number;
  title: string;
  description?: string;
  amount: number;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  expense_type: 'loan_emi' | 'rent' | 'subscription' | 'insurance' | 'utility' | 'other';
  start_date: string;
  end_date: string;
  due_day: number;
  next_due_date: string;
  next_due_date_formatted: string;
  is_overdue: boolean;
  days_until_due: number;
  monthly_amount: number;
  yearly_amount: number;
  remaining_payments: number;
  total_paid_so_far: number;
  payments_made: number;
  progress_percentage: number;
  auto_deduct: boolean;
  payment_method: 'upi' | 'neft' | 'rtgs' | 'imps' | 'auto_debit' | 'cheque' | 'cash' | 'credit_card' | 'debit_card' | 'other_method';
  reference_number?: string;
  category: {
    id: number;
    name: string;
    color: string;
  };
  bank?: {
    id: number;
    name: string;
    code: string;
  };
}

interface Category {
  id: number;
  name: string;
  color: string;
}



const PlannedExpensesPage: React.FC = () => {
  const [expenses, setExpenses] = useState<PlannedExpense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  
  // Safety check: ensure banks is always an array
  useEffect(() => {
    if (!Array.isArray(banks)) {
      console.warn('Banks is not an array, resetting to empty array');
      setBanks([]);
    }
  }, [banks]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<PlannedExpense | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    frequency: 'monthly' as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
    status: 'active' as 'active' | 'paused' | 'completed' | 'cancelled',
    expense_type: 'other' as 'loan_emi' | 'rent' | 'subscription' | 'insurance' | 'utility' | 'other',
    start_date: '',
    end_date: '',
    due_day: 1,
    auto_deduct: false,
    payment_method: 'other_method' as 'upi' | 'neft' | 'rtgs' | 'imps' | 'auto_debit' | 'cheque' | 'cash' | 'credit_card' | 'debit_card' | 'other_method',
    payment_details: '',
    reference_number: '',
    category_id: '',
    bank_id: ''
  });

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found, user not authenticated');
      setError('User not authenticated. Please log in.');
      window.location.href = '/login';
      return;
    }
    
    console.log('Token found, validating authentication...');
    validateAndFetchData();
  }, []);

  const validateAndFetchData = async () => {
    try {
      // First validate the token
      await authAPI.validateToken();
      console.log('Token validated successfully, fetching data...');
      fetchData();
    } catch (error: any) {
      console.error('Token validation failed:', error);
      setError('Session expired. Please log in again.');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching data...');
      console.log('Token:', localStorage.getItem('token'));
      
      // Fetch data with individual error handling
      let expensesData = [];
      let categoriesData = [];
      let banksData = [];
      
      try {
        const expensesRes = await plannedExpensesAPI.getAll();
        console.log('Expenses response:', expensesRes);
        expensesData = expensesRes.data.data || [];
      } catch (err) {
        console.error('Failed to fetch expenses:', err);
      }
      
      try {
        const categoriesRes = await categoriesAPI.getAll();
        console.log('Categories response:', categoriesRes);
        categoriesData = categoriesRes.data.data || categoriesRes.data || [];
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
      
      try {
        const banksRes = await banksAPI.getAll();
        console.log('Banks response:', banksRes);
        console.log('Banks response data:', banksRes.data);
        console.log('Banks response status:', banksRes.status);
        
        // Handle new API response format for banks
        banksData = banksRes.data.data || banksRes.data || [];
        console.log('Banks data extracted:', banksData);
        console.log('Banks data type:', typeof banksData);
        console.log('Is banks array:', Array.isArray(banksData));
        console.log('Banks length:', banksData.length);
      } catch (err) {
        console.error('Failed to fetch banks:', err);
        // If banks API fails, try to continue with empty array
        banksData = [];
      }
      
      // Set all the data
      setExpenses(expensesData);
      setCategories(categoriesData);
      setBanks(banksData);
      
      // Debug logging
      console.log('Final data loaded:');
      console.log('Banks loaded:', banksData);
      console.log('Categories loaded:', categoriesData);
      console.log('Expenses loaded:', expensesData);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      console.error('Error response:', error.response);
      
      // More specific error handling
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        // Redirect to login if not authenticated
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to view this data.');
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError(error.response?.data?.error || error.message || 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingExpense(null);
    setFormData({
      title: '',
      description: '',
      amount: '',
      frequency: 'monthly' as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
      status: 'active' as 'active' | 'paused' | 'completed' | 'cancelled',
      expense_type: 'other' as 'loan_emi' | 'rent' | 'subscription' | 'insurance' | 'utility' | 'other',
      start_date: '',
      end_date: '',
      due_day: 1,
      auto_deduct: false,
      payment_method: 'other_method' as 'upi' | 'neft' | 'rtgs' | 'imps' | 'auto_debit' | 'cheque' | 'cash' | 'credit_card' | 'debit_card' | 'other_method',
      payment_details: '',
      reference_number: '',
      category_id: '',
      bank_id: ''
    });
    setDialogOpen(true);
  };

  const handleEdit = (expense: PlannedExpense) => {
    setEditingExpense(expense);
    setFormData({
      title: expense.title,
      description: expense.description || '',
      amount: expense.amount.toString(),
      frequency: expense.frequency as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
      status: expense.status as 'active' | 'paused' | 'completed' | 'cancelled',
      expense_type: expense.expense_type as 'loan_emi' | 'rent' | 'subscription' | 'insurance' | 'utility' | 'other',
      start_date: expense.start_date,
      end_date: expense.end_date,
      due_day: expense.due_day,
      auto_deduct: expense.auto_deduct,
      payment_method: (expense.payment_method as 'upi' | 'neft' | 'rtgs' | 'imps' | 'auto_debit' | 'cheque' | 'cash' | 'credit_card' | 'debit_card' | 'other_method') || 'other_method',
      payment_details: expense.reference_number || '',
      reference_number: expense.reference_number || '',
      category_id: expense.category.id.toString(),
      bank_id: expense.bank?.id.toString() || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.amount || !formData.category_id || !formData.start_date || !formData.end_date) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setError('');
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        category_id: parseInt(formData.category_id),
        bank_id: formData.bank_id ? parseInt(formData.bank_id) : undefined,
        due_day: parseInt(formData.due_day.toString()),
        frequency: formData.frequency as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
        status: formData.status as 'active' | 'paused' | 'completed' | 'cancelled',
        expense_type: formData.expense_type as 'loan_emi' | 'rent' | 'subscription' | 'insurance' | 'utility' | 'other',
        payment_method: formData.payment_method as 'upi' | 'neft' | 'rtgs' | 'imps' | 'auto_debit' | 'cheque' | 'cash' | 'credit_card' | 'debit_card' | 'other_method',
        reference_number: formData.payment_details
      };

      if (editingExpense) {
        await plannedExpensesAPI.update(editingExpense.id, submitData);
        setSuccessMessage('Planned expense updated successfully');
      } else {
        await plannedExpensesAPI.create(submitData);
        setSuccessMessage('Planned expense created successfully');
      }

      setDialogOpen(false);
      fetchData();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to save planned expense');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this planned expense?')) {
      try {
        await plannedExpensesAPI.delete(id);
        setSuccessMessage('Planned expense deleted successfully');
        fetchData();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error: any) {
        setError(error.response?.data?.error || 'Failed to delete planned expense');
      }
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await plannedExpensesAPI.toggleStatus(id);
      fetchData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to toggle status');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <PlayArrow color="success" />;
      case 'paused': return <Pause color="warning" />;
      case 'completed': return <CheckCircle color="info" />;
      case 'cancelled': return <Cancel color="error" />;
      default: return <PlayArrow />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getExpenseTypeColor = (type: string) => {
    switch (type) {
      case 'loan_emi': return 'error';
      case 'rent': return 'warning';
      case 'subscription': return 'info';
      case 'insurance': return 'secondary';
      case 'utility': return 'primary';
      default: return 'default';
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      case 'quarterly': return 'Quarterly';
      case 'yearly': return 'Yearly';
      default: return frequency;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Planned Expenses</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Planned Expense
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {/* Show loading or dashboard */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Active
                  </Typography>
                  <Typography variant="h4">
                    {expenses && Array.isArray(expenses) ? expenses.filter(e => e.status === 'active').length : 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Monthly Total
                  </Typography>
                  <Typography variant="h4">
                    ₹{(() => {
                      try {
                        if (!expenses || !Array.isArray(expenses)) return '0.00';
                        const activeExpenses = expenses.filter(e => e.status === 'active');
                        if (activeExpenses.length === 0) return '0.00';
                        const total = activeExpenses.reduce((sum, e) => {
                          const monthlyAmount = typeof e.monthly_amount === 'number' ? e.monthly_amount : parseFloat(String(e.monthly_amount || 0)) || 0;
                          return sum + monthlyAmount;
                        }, 0);
                        // Ensure total is a valid number
                        if (isNaN(total) || !isFinite(total)) return '0.00';
                        return total.toFixed(2);
                      } catch (error) {
                        console.error('Error calculating monthly total:', error);
                        return '0.00';
                      }
                    })()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Upcoming
                  </Typography>
                  <Typography variant="h4">
                    {expenses && Array.isArray(expenses) ? expenses.filter(e => e.status === 'active' && !e.is_overdue).length : 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Overdue
                  </Typography>
                  <Typography variant="h4" color="error">
                    {expenses && Array.isArray(expenses) ? expenses.filter(e => e.status === 'active' && e.is_overdue).length : 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Expenses Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Next Due</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses && Array.isArray(expenses) ? expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{expense.title}</Typography>
                        {expense.description && (
                          <Typography variant="body2" color="textSecondary">
                            {expense.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={expense.expense_type.replace('_', ' ').toUpperCase()}
                        color={getExpenseTypeColor(expense.expense_type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">₹{(() => {
                        try {
                          const monthlyAmount = typeof expense.monthly_amount === 'number' ? expense.monthly_amount : parseFloat(String(expense.monthly_amount || 0)) || 0;
                          if (monthlyAmount > 0) {
                            return monthlyAmount.toFixed(2);
                          }
                          // Fallback to base amount if monthly_amount is 0
                          const amount = expense.amount;
                          if (typeof amount === 'number' && !isNaN(amount) && isFinite(amount) && amount > 0) {
                            return amount.toFixed(2);
                          }
                          return '0.00';
                        } catch (error) {
                          console.error('Error formatting expense amount:', error);
                          return '0.00';
                        }
                      })()}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {expense.frequency === 'monthly' ? 'Base Amount' : 'Monthly'} ₹{(() => {
                          const monthlyAmount = typeof expense.monthly_amount === 'number' ? expense.monthly_amount : parseFloat(String(expense.monthly_amount || 0)) || 0;
                          return monthlyAmount.toFixed(2);
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={getFrequencyLabel(expense.frequency)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {expense.next_due_date_formatted}
                        </Typography>
                        {expense.is_overdue ? (
                          <Chip
                            icon={<Warning />}
                            label={`${Math.abs(expense.days_until_due || 0)} days overdue`}
                            color="error"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<Schedule />}
                            label={`${expense.days_until_due || 0} days left`}
                            color="info"
                            size="small"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(expense.status)}
                        label={expense.status}
                        color={getStatusColor(expense.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: '100%' }}>
                        <LinearProgress
                          variant="determinate"
                          value={expense.progress_percentage}
                          sx={{ height: 8, borderRadius: 5 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {(() => {
                            try {
                              const progress = expense.progress_percentage;
                              if (typeof progress === 'number' && !isNaN(progress) && isFinite(progress)) {
                                return progress.toFixed(1);
                              }
                              return '0.0';
                            } catch (error) {
                              console.error('Error formatting progress percentage:', error);
                              return '0.0';
                            }
                          })()}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(expense)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={expense.status === 'active' ? 'Pause' : 'Activate'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(expense.id)}
                            disabled={expense.status === 'completed'}
                          >
                            {expense.status === 'active' ? <Pause /> : <PlayArrow />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(expense.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )) : null}
                {expenses && Array.isArray(expenses) && expenses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No planned expenses yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add/Edit Dialog */}
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingExpense ? 'Edit Planned Expense' : 'Add Planned Expense'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title *"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount *"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                inputProps={{ step: "0.01", min: "0" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category * - {categories.length} loaded</InputLabel>
                <Select
                  value={formData.category_id}
                  label="Category * - {categories.length} loaded"
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  disabled={loading}
                >
                  {loading ? (
                    <MenuItem disabled>Loading categories...</MenuItem>
                  ) : categories.length === 0 ? (
                    <MenuItem disabled>No categories available</MenuItem>
                  ) : (
                    categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Expense Type *</InputLabel>
                <Select
                  value={formData.expense_type}
                  label="Expense Type *"
                  onChange={(e) => setFormData({ ...formData, expense_type: e.target.value as 'loan_emi' | 'rent' | 'subscription' | 'insurance' | 'utility' | 'other' })}
                >
                  <MenuItem value="loan_emi">Loan EMI</MenuItem>
                  <MenuItem value="rent">Rent</MenuItem>
                  <MenuItem value="subscription">Subscription</MenuItem>
                  <MenuItem value="insurance">Insurance</MenuItem>
                  <MenuItem value="utility">Utility</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency *</InputLabel>
                <Select
                  value={formData.frequency}
                  label="Frequency *"
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' })}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Day *"
                type="number"
                value={formData.due_day}
                onChange={(e) => setFormData({ ...formData, due_day: parseInt(e.target.value) })}
                inputProps={{ min: "1", max: "31" }}
                helperText="Day of month when payment is due"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date *"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date *"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Bank (Optional) - {banks.length} banks loaded</InputLabel>
                <Select
                  value={formData.bank_id}
                  label="Bank (Optional) - {banks.length} banks loaded"
                  onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
                  disabled={loading}
                >
                  <MenuItem value="">No Bank</MenuItem>
                  {loading ? (
                    <MenuItem disabled>Loading banks...</MenuItem>
                  ) : banks.length === 0 ? (
                    <MenuItem disabled>No banks available</MenuItem>
                  ) : (
                    banks.map((bank) => (
                      <MenuItem key={bank.id} value={bank.id}>
                        {bank.name} ({bank.code})
                        {bank.credit_card && ' 💳'}
                        {bank.current_balance !== undefined && ` - ₹${bank.current_balance.toLocaleString()}`}
                      </MenuItem>
                    ))
                  )}
                </Select>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                  {banks.length} banks available • Select a bank to auto-deduct expenses
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method *</InputLabel>
                <Select
                  value={formData.payment_method}
                  label="Payment Method *"
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value as 'upi' | 'neft' | 'rtgs' | 'imps' | 'auto_debit' | 'cheque' | 'cash' | 'credit_card' | 'debit_card' | 'other_method' })}
                >
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="neft">NEFT</MenuItem>
                  <MenuItem value="rtgs">RTGS</MenuItem>
                  <MenuItem value="imps">IMPS</MenuItem>
                  <MenuItem value="auto_debit">Auto Debit</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                  <MenuItem value="debit_card">Debit Card</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                              <TextField
                  fullWidth
                  label="Payment Method Details"
                  value={formData.payment_details}
                  onChange={(e) => setFormData({ ...formData, payment_details: e.target.value })}
                  placeholder="e.g., UPI ID, Account number, etc."
                />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Additional details about this expense..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.auto_deduct}
                    onChange={(e) => setFormData({ ...formData, auto_deduct: e.target.checked })}
                  />
                }
                label="Auto-deduct from bank account"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingExpense ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default PlannedExpensesPage;
